/**
 * numericScrubInstall — wires numericScrub.js to every `<input type="number">`
 * on the page, once, by event delegation (installed from main.jsx). No
 * component opts in; `data-no-scrub` on an input opts out.
 *
 * Focus the field, put the cursor before a digit, scroll: the value steps by
 * that digit's place (numericScrub.js has the rules). ArrowUp / ArrowDown step
 * the same way.
 *
 * WHY THE FIELD BECOMES type="text" WHILE IT IS BEING EDITED: a number input
 * does not expose its caret (`selectionStart` is null on type="number" in every
 * browser), and the caret is the whole feature. So on a click into the text
 * the input is switched to text (inputmode="decimal") and switched back on
 * blur. React 19 re-assigns `element.type` from the prop on EVERY render of
 * the input, which would undo the swap after the first keystroke or step, so
 * while swapped the element carries its own `type` accessor that ignores
 * writes (removed again on blur). Its value handling is unaffected: React
 * compares a controlled input against its `type` PROP, not the DOM's, so the
 * number-input rule (letting "1." stand while typing) still applies. While
 * swapped, keystrokes that cannot be part of a number are refused, as the
 * number input would have refused them.
 *
 * NO SPINNER ARROWS. The browser's up/down triangles are hidden on every
 * number field (SLIDER_CSS): the drag slider, the wheel and the arrow keys
 * replace them, and they read as dated default UI (Florent, 2026-10-02).
 *
 * A value is written through the native setter and an `input` event, which is
 * what makes React's onChange see it — the same path typing takes.
 *
 * DRAG SLIDER (Unreal-style), on a field that is NOT focused:
 *   - hovering shows a ↔ cursor;
 *   - press and drag more than DRAG_START_PX slides the value: right up, left
 *     down, in steps of the value's own precision (sliderStep), faster for a
 *     faster hand (sliderGain), Shift ÷10, Ctrl ×10. The pointer is locked and
 *     hidden while sliding, so the drag never hits the screen edge, and it
 *     reappears where it started;
 *   - press and release WITHOUT moving is an ordinary click: the field takes
 *     focus with the caret where the click was, i.e. the scroll-edit mode above.
 * Once focused the field is a text field again (drag selects text); the slider
 * comes back after it loses focus. The press is taken over in the capture phase
 * (preventDefault + stopPropagation), so neither the browser's focus/caret nor a
 * node drag underneath sees it — the click path re-creates both on release.
 */
import {
  scrubValue, createWheelAccumulator,
  SLIDER_PX_PER_STEP, sliderStep, sliderGain, sliderValue,
} from './numericScrub.js'

const SWAPPED = 'scrubSwapped'
const NUMBER_CHAR = /^[0-9.eE+-]$/
// Movement (px) before a press becomes a slide rather than a click.
const DRAG_START_PX = 3

// Injected here so the feature is one module: the ↔ cursor over a number
// field that a drag would slide (a focused field keeps the text cursor), and
// no spinner arrows on any number field — WebKit/Blink draw them as
// pseudo-elements, Firefox through the field's appearance.
const SLIDER_CSS = `
input[type="number"]:not(:focus):not(:disabled):not([readonly]):not([data-no-scrub]) { cursor: ew-resize; }
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button { -webkit-appearance: none; appearance: none; margin: 0; }
input[type="number"] { -moz-appearance: textfield; appearance: textfield; }
`

let installed = false

export function installNumericScrub(doc = typeof document !== 'undefined' ? document : null) {
  if (!doc || installed) return
  installed = true

  // The click that focused a number field: the swap to text drops the caret
  // the click placed, so it is put back from this x on mouseup.
  let pendingCaretX = null
  const wheel = createWheelAccumulator(50)

  const eligible = (el) =>
    el instanceof HTMLInputElement && !el.disabled && !el.readOnly && !('noScrub' in el.dataset)

  const swapIn = (input) => {
    if (input.type !== 'number') return
    input.dataset[SWAPPED] = '1'
    typeProp.set.call(input, 'text')
    // Hold the swap against React re-applying type="number" on re-render.
    Object.defineProperty(input, 'type', {
      configurable: true,
      get() { return typeProp.get.call(this) },
      set() {},
    })
    input.inputMode = 'decimal'
  }

  const swapOut = (input) => {
    if (input.dataset[SWAPPED] !== '1') return
    delete input.dataset[SWAPPED]
    // A half-typed "1." is not a valid number and type=number would blank it.
    const n = parseFloat(input.value)
    if (input.value !== '' && Number.isFinite(n) && String(n) !== input.value && !/^-?\d*\.?\d+$/.test(input.value)) {
      setValue(input, String(n))
    }
    input.removeAttribute('inputmode')
    delete input.type
    typeProp.set.call(input, 'number')
  }

  const style = doc.createElement('style')
  style.textContent = SLIDER_CSS
  doc.head.appendChild(style)

  // ── Drag slider ───────────────────────────────────────────────────────────
  let press = null   // { input, x, travel, sliding, start, step, decimals, units, speed, t }

  const startSlide = (p) => {
    p.sliding = true
    const raw = p.input.value !== '' ? p.input.value
      : (Number.isFinite(parseFloat(p.input.placeholder)) ? p.input.placeholder : '0')
    const { step: st, decimals } = sliderStep(raw, attrNumber(p.input, 'step'))
    p.start = parseFloat(raw) || 0
    p.step = st
    p.decimals = decimals
    p.units = 0
    doc.body.style.cursor = 'ew-resize'
    // Infinite drag: lock + hide the pointer. If the browser refuses (no
    // transient activation, an iframe policy, a test runner), the slide still
    // works on movementX; it just stops at the screen edge.
    try {
      const r = p.input.requestPointerLock?.()
      if (r && typeof r.catch === 'function') r.catch(() => {})
    } catch { /* unlocked slide */ }
  }

  const slideMove = (e) => {
    const p = press
    if (!p) return
    const dx = e.movementX || 0
    if (!p.sliding) {
      p.travel += Math.abs(dx) + Math.abs(e.movementY || 0)
      if (p.travel < DRAG_START_PX) return
      startSlide(p)
    }
    const now = performance.now()
    const dt = Math.max(1, now - p.t)
    p.t = now
    p.speed = p.speed * 0.6 + (Math.abs(dx) / dt) * 0.4
    const mod = e.shiftKey ? 0.1 : (e.ctrlKey || e.metaKey) ? 10 : 1
    p.units += dx * sliderGain(p.speed) * mod / SLIDER_PX_PER_STEP
    const r = sliderValue(p.start, Math.trunc(p.units), p.step, p.decimals, {
      min: attrNumber(p.input, 'min'),
      max: attrNumber(p.input, 'max'),
    })
    if (r.text !== p.input.value) setValue(p.input, r.text)
  }

  // Drop the press and everything it armed. Also run when a new press finds
  // an old one still live (its mouseup landed outside the window).
  const release = (p) => {
    press = null
    doc.removeEventListener('mousemove', slideMove, true)
    doc.removeEventListener('mouseup', slideEnd, true)
    if (p.sliding) {
      doc.body.style.cursor = ''
      if (doc.pointerLockElement === p.input) doc.exitPointerLock?.()
    }
  }

  const slideEnd = (e) => {
    const p = press
    if (!p || e.button !== 0) return
    release(p)
    if (p.sliding) {
      // Fields that commit on `change` rather than on every input see one now.
      p.input.dispatchEvent(new Event('change', { bubbles: true }))
      return
    }
    // A click, not a slide: what the press would have done had it not been
    // taken over — focus, with the caret where the click landed.
    p.input.focus()
    if (p.input.dataset[SWAPPED] === '1') {
      const i = caretFromX(p.input, p.x)
      p.input.setSelectionRange(i, i)
    }
  }

  doc.addEventListener('mousedown', (e) => {
    const input = e.target
    if (!eligible(input) || input.type !== 'number') return
    if (e.button === 0 && input !== doc.activeElement) {
      // Unfocused: this press is a slide or a click, decided by movement.
      e.preventDefault()
      e.stopPropagation()
      if (press) release(press)
      press = { input, x: e.clientX, travel: 0, sliding: false, speed: 0, t: performance.now() }
      doc.addEventListener('mousemove', slideMove, true)
      doc.addEventListener('mouseup', slideEnd, true)
      return
    }
    // Focused: only note the x; the swap waits for focusin. Changing the type
    // during mousedown replaces the field's box under the click and the
    // browser drops the focus the click would have given it.
    pendingCaretX = e.clientX
  }, true)

  doc.addEventListener('mouseup', (e) => {
    const input = e.target
    const x = pendingCaretX
    pendingCaretX = null
    if (x === null || !eligible(input) || input.dataset[SWAPPED] !== '1') return
    // A drag that selected text is left alone.
    if (input.selectionStart !== input.selectionEnd) return
    const i = caretFromX(input, x)
    input.setSelectionRange(i, i)
  }, true)

  // ── Not-yet-a-number guard ─────────────────────────────────────────────────
  // Clearing a field to retype it passes through states that are not numbers:
  // "" (all deleted), "-", ".", "-.". Every node's onChange parses the text, so
  // those reached the node as NaN — stored, and shown back in the box as the
  // literal text "NaN" instead of an empty field with the caret in it. So while
  // a field is being edited, an input/change whose text is not a number yet is
  // stopped here (capture, before React's root listener) and never reaches
  // onChange: the box shows what was typed, the node keeps its last value, and
  // the next keystroke that makes a number goes through as normal. Leaving the
  // field still not-a-number puts back the value it had when editing began —
  // and tells the node, since digits deleted on the way ("4.5" → "4." → "4")
  // were live edits it already received. Clear-and-click-away changes nothing.
  // Fields where blank MEANS something — a numeric placeholder ("blank = the
  // 0.3 default") or `data-allow-empty` ("blank = unset") — still pass "".
  //
  // HOLDING THE DRAFT AGAINST REACT. Stopping the event is only half of it: the
  // node still holds the old number, and a node card re-renders all the time
  // (live readouts), so React re-asserts that number into the box and the
  // deleted digit reappears. So while a draft stands, the field carries its own
  // `value` accessor that ignores writes — the same trick swapIn uses for
  // `type`. The accessor React's value tracker installed is put back the moment
  // the text is a number again (before React sees that input event) or the
  // field is left. Our own writes (setValue) use the prototype setter and are
  // unaffected.
  const atFocus = new WeakMap()
  const heldValueDesc = new WeakMap() // input -> the own `value` descriptor it had before (React's tracker)
  const holdDraft = (input) => {
    if (heldValueDesc.has(input)) return
    heldValueDesc.set(input, Object.getOwnPropertyDescriptor(input, 'value') ?? null)
    Object.defineProperty(input, 'value', {
      configurable: true,
      get() { return nativeValueGetter.call(this) },
      set() { /* a re-render re-asserting the old number over the user's draft — ignored */ },
    })
  }
  const releaseDraft = (input) => {
    if (!heldValueDesc.has(input)) return
    const prev = heldValueDesc.get(input)
    heldValueDesc.delete(input)
    delete input.value
    if (prev) Object.defineProperty(input, 'value', prev)
  }
  const editing = (el) => eligible(el) && el.dataset[SWAPPED] === '1'
  const blankable = (el) => 'allowEmpty' in el.dataset || el.placeholder !== ''
  const notANumberYet = (el) =>
    !Number.isFinite(parseFloat(el.value)) && !(el.value === '' && blankable(el))

  const guard = (e) => {
    const input = e.target
    if (!editing(input)) return
    if (notANumberYet(input)) {
      e.stopImmediatePropagation()
      holdDraft(input)
    } else {
      releaseDraft(input) // a number again: let React's writes (and this event) through
    }
  }
  doc.addEventListener('input', guard, true)
  doc.addEventListener('change', guard, true)

  doc.addEventListener('focusin', (e) => {
    const input = e.target
    if (!eligible(input)) return
    swapIn(input)
    atFocus.set(input, input.value)
  }, true)

  doc.addEventListener('focusout', (e) => {
    const input = e.target
    if (!eligible(input)) return
    // Left blank / half-typed: back to the value from when editing began, with
    // an input event so the node gets it too (setValue).
    const restore = input.dataset[SWAPPED] === '1' && notANumberYet(input) && atFocus.has(input)
    releaseDraft(input)
    if (restore) setValue(input, atFocus.get(input))
    swapOut(input)
  }, true)

  doc.addEventListener('beforeinput', (e) => {
    const input = e.target
    if (!eligible(input) || input.dataset[SWAPPED] !== '1') return
    if (e.inputType === 'insertText' && e.data && ![...e.data].every(c => NUMBER_CHAR.test(c))) {
      e.preventDefault()
    }
  }, true)

  // Capture + non-passive: this runs before React Flow's canvas zoom and the
  // page scroll, and can cancel both while a field is being scrubbed.
  doc.addEventListener('wheel', (e) => {
    const input = e.target
    if (!eligible(input) || input !== doc.activeElement || input.dataset[SWAPPED] !== '1') return
    e.preventDefault()
    e.stopPropagation()
    const px = e.deltaMode === 1 ? e.deltaY * 33 : e.deltaMode === 2 ? e.deltaY * 400 : e.deltaY
    const steps = wheel(px)
    for (let i = 0; i < Math.abs(steps); i++) step(input, steps > 0 ? 1 : -1)
  }, { capture: true, passive: false })

  doc.addEventListener('keydown', (e) => {
    const input = e.target
    if (!eligible(input) || input.dataset[SWAPPED] !== '1') return
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return
    e.preventDefault()
    step(input, e.key === 'ArrowUp' ? 1 : -1)
  }, true)
}

function step(input, direction) {
  let text = input.value
  let caret = input.selectionStart ?? text.length
  // A blank field with a numeric placeholder (e.g. a transition's Blend,
  // where blank means "the 0.3 default") scrubs from the placeholder.
  if (text === '' && input.placeholder && Number.isFinite(parseFloat(input.placeholder))) {
    text = input.placeholder
    caret = text.length
  }
  const r = scrubValue(text, caret, direction, {
    min: attrNumber(input, 'min'),
    max: attrNumber(input, 'max'),
    minPower: wholeNumbersOnly(input) ? 0 : -Infinity,
  })
  if (!r) return
  setValue(input, r.text)
  input.setSelectionRange(r.caret, r.caret)
}

let _measure = null
/**
 * The caret index nearest to a click at `clientX`, by measuring the text with
 * the field's own font. Honours padding, border, text-align and horizontal
 * scroll; letter-spacing is ignored (no number field here sets it).
 */
function caretFromX(input, clientX) {
  const cs = getComputedStyle(input)
  _measure ??= document.createElement('canvas').getContext('2d')
  _measure.font = `${cs.fontStyle} ${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`
  const text = input.value
  const rect = input.getBoundingClientRect()
  const left = rect.left + parseFloat(cs.borderLeftWidth) + parseFloat(cs.paddingLeft)
  const right = rect.right - parseFloat(cs.borderRightWidth) - parseFloat(cs.paddingRight)
  const width = _measure.measureText(text).width
  const align = cs.textAlign
  let x0 = align === 'right' || align === 'end' ? right - width
    : align === 'center' ? (left + right - width) / 2
    : left
  x0 -= input.scrollLeft
  let best = 0
  let bestD = Infinity
  for (let i = 0; i <= text.length; i++) {
    const d = Math.abs(x0 + _measure.measureText(text.slice(0, i)).width - clientX)
    if (d < bestD) { bestD = d; best = i }
  }
  return best
}

// A field whose step is a whole number (step="1", step="5") takes whole
// numbers only, so the cursor-at-end rule must not add a decimal to it.
// No step attribute means the browser's default of 1 on a number input, but
// most fields here leave it unset while holding decimals, so only an explicit
// whole step counts.
function wholeNumbersOnly(input) {
  const step = attrNumber(input, 'step')
  return Number.isFinite(step) && step >= 1 && Number.isInteger(step)
}

function attrNumber(input, name) {
  const v = input.getAttribute(name)
  return v === null || v === '' ? undefined : parseFloat(v)
}

const typeProp = typeof HTMLInputElement !== 'undefined'
  ? Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'type')
  : null

const nativeValueSetter = typeof HTMLInputElement !== 'undefined'
  ? Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set
  : null
const nativeValueGetter = typeof HTMLInputElement !== 'undefined'
  ? Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').get
  : null

function setValue(input, text) {
  nativeValueSetter.call(input, text)
  input.dispatchEvent(new Event('input', { bubbles: true }))
}
